import { Controller } from "@hotwired/stimulus"

// Creative::SPIN: one {a|b} group, no nesting.
const SPIN = /\{([^{}]*)\}/g

// Drives the campaign form: filterable list picker, one textarea per creative (add/remove),
// creative / domain counts with the suggested numbers for the chosen list (list size ÷ rotation
// interval, capped by max_queue_size; with spintax the creative count is what the save
// requires), the Write / Auto generate creatives toggle (creative_mode; the generate side shows how
// many creatives Serin will write for the list), and schedule time toggling.
export default class extends Controller {
  static targets = ["list", "listFilter", "creatives", "creativeRow", "creativeIndex", "creativeTemplate", "creativeCount",
                    "creativeWarning", "wordingPanel", "domains", "domainCount", "maxQueue", "date", "time",
                    "mode", "modeButton", "writeSection", "generateSection", "generateCount"]
  static values = { sizes: Object, rotation: Number, domainInterval: Number, keywords: Array, wording: Object, maxCreatives: Number,
                    requireCreatives: Boolean, requireDomains: Boolean }

  connect() {
    this.keywordRegex = this.buildKeywordRegex(this.keywordsValue)
    this.wording = this.wordingValue || {}
    this.stopwords = new Set(this.wording.stopwords || [])
    this.applyMode()
    this.update()
    this.toggleTime()
  }

  // ---- write / generate ----------------------------------------------------------------------
  setMode(event) {
    if (!this.hasModeTarget) return
    this.modeTarget.value = event.params.mode
    this.applyMode()
    this.update()
  }

  applyMode() {
    if (!this.hasModeTarget) return
    const mode = this.modeTarget.value === "generate" ? "generate" : "write"
    if (this.hasWriteSectionTarget) this.writeSectionTarget.hidden = mode === "generate"
    if (this.hasGenerateSectionTarget) this.generateSectionTarget.hidden = mode !== "generate"
    this.modeButtonTargets.forEach(b => {
      const active = b.dataset.campaignFormModeParam === mode
      b.classList.toggle("seg-item-active", active)
      b.setAttribute("aria-selected", active)
    })
  }

  // "100 creatives will be generated · one per 100 recipients of the list (10,000 ÷ 100)": the
  // same number the save asks Serin for (Campaigns::Save → creatives_needed).
  generateSummary(size) {
    if (!size || !this.rotationValue) return "Choose a list: Serin writes one creative per rotation interval of its recipients."
    let wanted = Math.max(1, Math.ceil(size / this.rotationValue))
    if (this.maxCreativesValue > 0) wanted = Math.min(wanted, this.maxCreativesValue)
    const strong = document.createElement("span")
    strong.className = "font-medium"
    strong.textContent = `${wanted.toLocaleString()} creative${wanted === 1 ? "" : "s"} will be generated`
    const rest = document.createElement("span")
    rest.className = "text-ink-2"
    rest.textContent = ` · one per ${this.rotationValue.toLocaleString()} recipients of the list (${size.toLocaleString()} ÷ ${this.rotationValue.toLocaleString()})`
    return [strong, rest]
  }

  // ---- flagged keywords ----------------------------------------------------------------------
  // Mirrors FlaggedKeyword::Matcher: case-insensitive, not adjacent to a letter or digit, so
  // "free" hits "FREE!" but not "freedom". Warns only; the server repeats the check at save.
  buildKeywordRegex(keywords) {
    const words = (keywords || []).map(k => String(k).trim()).filter(Boolean)
    if (!words.length) return null
    const escaped = words.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/ /g, "\\s+"))
    return new RegExp(`(?<![\\p{L}\\p{N}])(?:${escaped.join("|")})(?![\\p{L}\\p{N}])`, "giu")
  }

  keywordHits(text) {
    if (!this.keywordRegex) return []
    const seen = new Set()
    for (const m of text.matchAll(this.keywordRegex)) seen.add(m[0].toLowerCase().replace(/\s+/g, " "))
    return this.keywordsValue.filter(k => seen.has(String(k).toLowerCase()))
  }

  // One line under each creative: its flagged keywords and the words it repeats.
  updateKeywordWarnings(repeats = []) {
    this.creativeRowTargets.forEach((row, i) => {
      const warn = row.querySelector("[data-campaign-form-target='creativeWarning']")
      if (!warn) return
      const hits = this.keywordHits(row.querySelector("textarea").value)
      const parts = []
      if (hits.length) parts.push(`Flagged: ${hits.join(", ")}`)
      if (repeats[i]?.length) parts.push(`Repeats within this creative: ${repeats[i].map(([w, n]) => `“${w}” ×${n}`).join(", ")}`)
      warn.hidden = parts.length === 0
      warn.lastElementChild.textContent = parts.join(" · ")
    })
  }

  // ---- repeated wording --------------------------------------------------------------------
  // Mirrors Campaigns::RepeatedWording on the typed text (its rules arrive in the wording value):
  // spintax groups are removed, since their alternatives are varied wording by design; the save
  // checks the expanded creatives. Warns only.
  wordingTokens(text) {
    const t = text.toLowerCase().replace(/’/g, "'").replace(/\[(?:link|url|first_name|last_name)\]/gi, " ").replace(SPIN, " ")
    return t.split(/[.!?;:,\n]+/).map(c => (c.match(/\p{L}[\p{L}']*/gu) || []).map(w => w.replace(/^'+|'+$/g, "")).filter(Boolean)).filter(c => c.length)
  }

  isContent(w) { return w.length >= (this.wording.minWord || 4) && !this.stopwords.has(w) }

  wordingCandidates(clauses) {
    const cands = new Set(), freq = new Map(), max = this.wording.maxPhrase || 4
    for (const words of clauses) {
      for (const w of words) if (this.isContent(w)) freq.set(w, (freq.get(w) || 0) + 1)
      for (let i = 0; i < words.length; i++) {
        for (let n = 1; n <= max && i + n <= words.length; n++) {
          const slice = words.slice(i, i + n)
          let a = 0, b = slice.length
          while (a < b && this.stopwords.has(slice[a])) a++
          while (b > a && this.stopwords.has(slice[b - 1])) b--
          const trimmed = slice.slice(a, b)
          if (!trimmed.length || !trimmed.some(w => this.isContent(w))) continue
          cands.add(trimmed.join(" "))
        }
      }
    }
    // The whole creative, stopwords trimmed; untrimmed when it holds no content word, so identical
    // "Hello [link]" creatives still share "hello".
    const words = clauses.flat()
    let a = 0, b = words.length
    while (a < b && this.stopwords.has(words[a])) a++
    while (b > a && this.stopwords.has(words[b - 1])) b--
    let whole = words.slice(a, b)
    if (!whole.some(w => this.isContent(w))) whole = words
    if (whole.length) cands.add(whole.join(" "))
    return [cands, freq]
  }

  analyzeWording(bodies) {
    const per = bodies.map(b => this.wordingCandidates(this.wordingTokens(b)))
    const total = bodies.filter(b => b.trim()).length, counts = new Map()
    for (const [cands] of per) for (const c of cands) counts.set(c, (counts.get(c) || 0) + 1)
    let phrases = []
    if (total >= (this.wording.minCreatives || 4)) {
      const warnAt = Math.ceil(total * (this.wording.warnShare || 0.5)), highAt = Math.ceil(total * (this.wording.highShare || 0.8))
      const qualifying = [...counts].filter(([, n]) => n >= warnAt)
      const maximal = qualifying.filter(([text, n]) => !qualifying.some(([other, m]) => other.length > text.length && m >= n && ` ${other} `.includes(` ${text} `)))
      phrases = maximal.sort((a, b) => b[1] - a[1] || b[0].split(" ").length - a[0].split(" ").length || a[0].localeCompare(b[0]))
                       .slice(0, this.wording.maxPhrases || 5).map(([text, n]) => ({ text, count: n, total, high: n >= highAt }))
    }
    const minRepeat = this.wording.repeatInOne || 3
    const repeats = per.map(([, freq]) => [...freq].filter(([, n]) => n >= minRepeat).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])))
    return { phrases, repeats }
  }

  // The panel under the creatives: one line per phrase with how many creatives share it.
  renderWordingPanel(phrases) {
    if (!this.hasWordingPanelTarget) return
    const panel = this.wordingPanelTarget
    panel.replaceChildren()
    panel.hidden = phrases.length === 0
    if (panel.hidden) return
    panel.className = "alert alert-warning mt-3 mb-0"
    panel.setAttribute("role", "status")
    const iconHolder = document.createElement("span")
    iconHolder.className = "mt-px"
    const icon = this.creativeWarningTargets[0]?.firstElementChild
    if (icon) iconHolder.appendChild(icon.cloneNode(true))
    const body = document.createElement("div")
    body.className = "flex-1"
    const title = document.createElement("div")
    title.className = "font-semibold"
    title.textContent = "Repeated wording — likely to be filtered"
    const intro = document.createElement("div")
    intro.className = "text-[13px] mt-0.5"
    intro.textContent = "Carriers filter campaigns whose messages share the same phrases. Reword these, or offer alternatives with spintax {a|b|c}:"
    const list = document.createElement("ul")
    list.className = "mt-2 flex flex-col gap-1.5 text-[13px] list-none p-0 m-0"
    for (const p of phrases) {
      const li = document.createElement("li")
      li.className = "flex items-center gap-2"
      const badge = document.createElement("span")
      badge.className = `badge ${p.high ? "badge-danger" : "badge-warning"}`
      badge.textContent = `${p.count} of ${p.total} creatives`
      const code = document.createElement("code")
      code.textContent = p.text
      li.append(badge, code)
      list.appendChild(li)
    }
    const note = document.createElement("div")
    note.className = "text-xs text-ink-3 mt-2"
    const high = Math.round((this.wording.highShare || 0.8) * 100), warn = Math.round((this.wording.warnShare || 0.5) * 100)
    note.textContent = `A word or phrase in ${high}% or more of the creatives is red, in ${warn}% or more amber; common words, the tags and spintax alternatives are ignored. Checked again when you save; it warns, it never blocks.`
    body.append(title, intro, list, note)
    panel.append(iconHolder, body)
  }

  // ---- list picker -------------------------------------------------------------------------
  filterLists() {
    if (!this.hasListTarget || this.listTarget.tagName !== "SELECT") return
    const q = this.listFilterTarget.value.trim().toLowerCase()
    let firstVisible = null
    for (const opt of this.listTarget.options) {
      const match = !q || opt.textContent.toLowerCase().includes(q)
      opt.hidden = !match
      opt.disabled = !match
      if (match && !firstVisible) firstVisible = opt
    }
    if (this.listTarget.selectedOptions[0]?.hidden && firstVisible) {
      this.listTarget.value = firstVisible.value
    }
    // No match: keep the current selection submittable rather than disabling every option,
    // which would drop campaign[list_id] from the form and fail with "List must be chosen".
    if (!firstVisible) {
      const current = this.listTarget.selectedOptions[0] || this.listTarget.options[0]
      if (current) { current.hidden = false; current.disabled = false }
    }
    this.listFilterTarget.setCustomValidity(firstVisible || !q ? "" : "No list matches this filter")
    this.update()
  }

  // ---- creatives ---------------------------------------------------------------------------
  addCreative() {
    this.creativesTarget.appendChild(this.creativeTemplateTarget.content.cloneNode(true))
    const rows = this.creativeRowTargets
    rows[rows.length - 1].querySelector("textarea").focus()
    this.update()
  }

  removeCreative(event) {
    const row = event.currentTarget.closest("[data-campaign-form-target='creativeRow']")
    if (this.creativeRowTargets.length === 1) {
      row.querySelector("textarea").value = ""
    } else {
      row.remove()
    }
    this.update()
  }

  // ---- counts + suggestions ----------------------------------------------------------------
  update() {
    this.creativeIndexTargets.forEach((el, i) => { el.textContent = `${i + 1}.` })
    const wording = this.analyzeWording(this.creativeRowTargets.map(r => r.querySelector("textarea").value))
    this.updateKeywordWarnings(wording.repeats)
    this.renderWordingPanel(wording.phrases)
    const size = this.listSize()
    if (this.hasCreativeCountTarget) {
      const bodies = this.creativeRowTargets.map(r => r.querySelector("textarea").value.trim()).filter(Boolean)
      const counts = bodies.map(b => this.variantCount(b))
      const variants = counts.some(c => c !== null) ? counts.reduce((sum, c) => sum + (c ?? 1), 0) : null
      this.creativeCountTarget.textContent = this.summary(bodies.length, "creative", size, this.rotationValue,
                                                          { variants, cap: this.maxCreativesValue, required: this.requireCreativesValue })
    }
    if (this.hasDomainCountTarget) {
      const n = this.domainsTarget.value.split(/[\s,;]+/).filter(d => d.trim()).length
      this.domainCountTarget.textContent = this.summary(n, "domain", size, this.domainIntervalValue, { required: this.requireDomainsValue }) + " "
    }
    if (this.hasGenerateCountTarget) {
      const summary = this.generateSummary(size)
      this.generateCountTarget.replaceChildren(...(Array.isArray(summary) ? summary : [summary]))
    }
  }

  listSize() {
    const raw = this.hasListTarget ? this.sizesValue[this.listTarget.value] : null
    if (!raw) return null
    const cap = this.hasMaxQueueTarget ? parseInt(this.maxQueueTarget.value, 10) : 0
    return cap > 0 ? Math.min(raw, cap) : raw
  }

  // Mirrors Creative.variant_count: the option counts of every {a|b} group multiplied; null for
  // a body without spintax. Unbalanced braces are the server's to refuse.
  variantCount(text) {
    let n = null
    for (const m of text.matchAll(SPIN)) n = (n ?? 1) * m[1].split("|").length
    return n
  }

  // "3 creatives · suggested for this list: 100 (10,000 recipients ÷ 100)". With spintax the
  // expanded count is shown too and the number is what the save requires (Campaigns::Save refuses
  // a spintax that yields fewer), so it reads "needed"; when the account requires the count
  // (enforce_creative_count / enforce_domain_count) it reads "required".
  summary(n, noun, size, interval, { variants = null, cap = 0, required = false } = {}) {
    let text = `${n} ${noun}${n === 1 ? "" : "s"}`
    if (variants !== null) text += ` (${variants.toLocaleString()} after spintax)`
    if (size && interval) {
      let wanted = Math.max(1, Math.ceil(size / interval))
      if (cap > 0) wanted = Math.min(wanted, cap)
      const word = required ? "required" : variants === null ? "suggested" : "needed"
      text += ` · ${word} for this list: ${wanted.toLocaleString()} (${size.toLocaleString()} recipients ÷ ${interval})`
    }
    return text
  }

  // ---- schedule -----------------------------------------------------------------------------
  toggleTime() {
    if (!this.hasDateTarget || !this.hasTimeTarget) return
    const manual = this.dateTarget.value === ""
    this.timeTarget.disabled = manual
    this.timeTarget.closest(".field").hidden = manual
  }
}
